import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { useCallback, useState } from 'react'
import useAuth from '@/features/auth/hooks/use-auth'
import { queryKeys } from '@/shared/constants/query-keys'
import type { CreditSort } from '../models/credit-list'
import {
  type CreditsAdminV2Query,
  creditsAdminV2Service
} from '../services/credits-admin-v2.service'

type UseCreditsAdminV2QueryArgs = {
  filters?: Record<string, unknown>
  sort?: CreditSort
  search?: string
  pageSize: number
}

export function useCreditsAdminV2Query({
  filters,
  sort,
  search,
  pageSize
}: UseCreditsAdminV2QueryArgs) {
  const { auth } = useAuth()
  const accessToken = auth?.v2AccessToken ?? ''
  const resetKey = JSON.stringify({ filters, sort, search, pageSize })
  const [cursorState, setCursorState] = useState<{
    key: string
    stack: (string | undefined)[]
  }>({ key: resetKey, stack: [undefined] })

  if (cursorState.key !== resetKey) {
    setCursorState({ key: resetKey, stack: [undefined] })
  }
  const after = cursorState.stack[cursorState.stack.length - 1]
  const listQuery: CreditsAdminV2Query = { filters, sort, search, pageSize, after }

  const query = useQuery({
    queryKey: queryKeys.credits.adminListV2({ filters, sort, search, pageSize, after }),
    queryFn: async () => await creditsAdminV2Service.list(accessToken, listQuery),
    enabled: Boolean(accessToken),
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
    retry: false
  })

  const nextCursor = query.data?.pagination.nextCursor
  const goNext = useCallback(() => {
    if (!nextCursor) return
    setCursorState((state) => ({ ...state, stack: [...state.stack, nextCursor] }))
  }, [nextCursor])
  const goPrev = useCallback(() => {
    setCursorState((state) =>
      state.stack.length > 1 ? { ...state, stack: state.stack.slice(0, -1) } : state
    )
  }, [])

  return {
    query,
    rows: query.data?.data ?? [],
    page: cursorState.stack.length,
    canPrev: cursorState.stack.length > 1,
    canNext: query.data?.pagination.hasMore ?? false,
    goNext,
    goPrev
  }
}

export function useCreditsAdminV2Summary(args: {
  filters?: Record<string, unknown>
  search?: string
}) {
  const { auth } = useAuth()
  const accessToken = auth?.v2AccessToken ?? ''
  return useQuery({
    queryKey: queryKeys.credits.summaryV2(args),
    queryFn: async () => await creditsAdminV2Service.summary(accessToken, args),
    enabled: Boolean(accessToken),
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
    retry: false
  })
}

export function useCreditDetailQuery(id: string | null | undefined) {
  const { auth } = useAuth()
  const accessToken = auth?.v2AccessToken ?? ''
  return useQuery({
    queryKey: queryKeys.credits.detailV2(id ?? null),
    queryFn: async () => await creditsAdminV2Service.detail(accessToken, id ?? ''),
    enabled: Boolean(accessToken) && Boolean(id),
    // El expediente se edita desde varias pestañas y sesiones: lo que esta pestaña tiene en caché
    // puede estar atrasado. Con el foco de vuelta se relee si pasaron más de `staleTime`, así que
    // la otra pestaña se pone al día sola, sin recargar y sin pedir el detalle en cada foco.
    staleTime: 10_000,
    refetchOnWindowFocus: true,
    retry: false
  })
}
